import React, { useState, useEffect } from 'react';
import { 
  Package, Plus, Edit, Trash2, Eye, EyeOff, Save, X, Image, Video, Printer,
  Search, Filter, Grid, List, Download, Upload, Copy, Tag, AlertCircle, FileSpreadsheet
} from 'lucide-react';
import { useMessageBox } from '../../../components/MessageBox';


const GestionProduits = () => {
  // ✅ Hook MessageBox
  const { showMessage, showConfirm, confirmDelete } = useMessageBox();

  // ============================================================================
  // STATES
  // ============================================================================
  const [produits, setProduits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [showMediaModal, setShowMediaModal] = useState(false);
  
  // États pour filtres et recherche
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('all');
  const [filterActif, setFilterActif] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [filterOrigine, setFilterOrigine] = useState('all');
  
  // Vue tableau/grille
  const [viewMode, setViewMode] = useState('table'); // 'table' ou 'grid'

  const [importing, setImporting] = useState(false);
  const fileInputRef = React.useRef(null);
  
// Toast notifications
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  
  const [formData, setFormData] = useState({
    designation: '',
    categorie_id: '',
    origine: 'ferme',
    fournisseur: '',
    unite: 'piece',
    prix_ttc: '',
    quantite_stock_depart: '',
    description: '',
    conseils_cuisson: '',
    recette_producteur: '',
    image_lien: '',
    actif: 'oui'
  });

  const STANDBY_IMAGE = '/uploads/produits/standby.png';

  // ============================================================================
  // TOAST NOTIFICATIONS
  // ============================================================================
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // ============================================================================
  // CHARGEMENT DONNÉES
  // ============================================================================
  const loadProduits = async () => {
    try {
      const response = await fetch('/api/produits');
      const data = await response.json();
      
      if (data.success) {
        setProduits(data.data);
      }
    } catch (error) {
      showToast('Erreur chargement produits', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch('/api/categories');
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.data);
      }
    } catch (error) {
      console.error('Erreur chargement catégories:', error);
    }
  };

  useEffect(() => {
    loadProduits();
    loadCategories();
  }, []);

  // ============================================================================
  // FILTRAGE ET RECHERCHE
  // ============================================================================
  const filteredProduits = produits.filter(produit => {
    // Recherche texte
    const matchSearch = searchTerm === '' || 
      produit.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
      produit.code_produit.includes(searchTerm);
    
    // Filtre catégorie
    const matchCategorie = filterCategorie === 'all' || 
      (filterCategorie === 'none' && !produit.categorie_id) ||
      produit.categorie_id === parseInt(filterCategorie);
    
    // Filtre actif
    const matchActif = filterActif === 'all' || 
      produit.actif === filterActif;
    
    // Filtre stock
    const matchStock = filterStock === 'all' ||
      (filterStock === 'low' && produit.quantite_disponible <= 5) ||
      (filterStock === 'medium' && produit.quantite_disponible > 5 && produit.quantite_disponible <= 20) ||
      (filterStock === 'high' && produit.quantite_disponible > 20);
    
    // Filtre origine
    const matchOrigine = filterOrigine === 'all' || 
      produit.origine === filterOrigine;
    
    return matchSearch && matchCategorie && matchActif && matchStock && matchOrigine;
  });

  // ============================================================================
  // GESTION FORMULAIRE
  // ============================================================================
  const resetForm = () => {
    setFormData({
      designation: '',
      categorie_id: '',
      origine: 'ferme',
      fournisseur: '',
      unite: 'piece',
      prix_ttc: '',
      quantite_stock_depart: '',
      description: '',
      conseils_cuisson: '',
      recette_producteur: '',
      image_lien: '',
      actif: 'oui'
    });
    setSelectedProduit(null);
  };

  const handleCreate = () => {
    resetForm();
    setModalMode('create');
    setShowModal(true);
  };

  const handleEdit = (produit) => {
    setFormData({
      designation: produit.designation || '',
      categorie_id: produit.categorie_id || '',
      origine: produit.origine || 'ferme',
      fournisseur: produit.fournisseur || '',
      unite: produit.unite || 'piece',
      prix_ttc: produit.prix_ttc || '',
      quantite_stock_depart: produit.quantite_stock_depart || '',
      description: produit.description || '',
      conseils_cuisson: produit.conseils_cuisson || '',
      recette_producteur: produit.recette_producteur || '',
      image_lien: produit.image_lien || '',
      actif: produit.actif || 'oui'
    });
    setSelectedProduit(produit);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleView = (produit) => {
    setSelectedProduit(null);
    setTimeout(() => {
      setSelectedProduit(produit);
      setModalMode('view');
      setShowModal(true);
    }, 10);
  };

  // 📋 DUPLIQUER un produit
  const handleDuplicate = (produit) => {
    setFormData({
      designation: `${produit.designation} (copie)`,
      categorie_id: produit.categorie_id || '',
      origine: produit.origine || 'ferme',
      fournisseur: produit.fournisseur || '',
      unite: produit.unite || 'piece',
      prix_ttc: produit.prix_ttc || '',
      quantite_stock_depart: produit.quantite_stock_depart || '',
      description: produit.description || '',
      conseils_cuisson: produit.conseils_cuisson || '',
      recette_producteur: produit.recette_producteur || '',
      image_lien: produit.image_lien || '',
      actif: 'oui'
    });
    setModalMode('create');
    setShowModal(true);
    showToast('Produit dupliqué ! Modifiez et enregistrez', 'info');
  };

  const handleSave = async () => {
    const url = modalMode === 'create' 
      ? '/api/produits'
      : `/api/produits/${selectedProduit.id}`;
    
    const method = modalMode === 'create' ? 'POST' : 'PUT';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        await loadProduits();
        setShowModal(false);
        resetForm();
        showToast(`Produit ${modalMode === 'create' ? 'créé' : 'modifié'} avec succès !`, 'success');
      } else {
        showToast(`Erreur: ${data.message}`, 'error');
      }
    } catch (error) {
      showToast(`Erreur réseau: ${error.message}`, 'error');
    }
  };

  const handleDelete = async (produit) => {
    const ok = await confirmDelete(`Supprimer le produit "${produit.designation}" ?`);
    if (!ok) return;

    try {
      const response = await fetch(`/api/produits/${produit.id}`, {
        method: 'DELETE'
      });

      const data = await response.json();

      if (data.success) {
        await loadProduits();
        showMessage('success', 'Produit supprimé avec succès');
      } else {
        showMessage('error', `Erreur: ${data.message}`);
      }
    } catch (error) {
      showMessage('error', `Erreur réseau: ${error.message}`);
    }
  };

  const handleToggleActif = async (produit) => {
    try {
      const response = await fetch(`/api/produits/${produit.id}/toggle-actif`, {
        method: 'PUT'
      });

      const data = await response.json();

      if (data.success) {
        await loadProduits();
        showToast(`Produit ${data.data.actif === 'oui' ? 'activé' : 'désactivé'}`, 'success');
      } else {
        showToast(`Erreur: ${data.message}`, 'error');
      }
    } catch (error) {
      showToast(`Erreur réseau: ${error.message}`, 'error');
    }
  };

  const handleStockAdjust = async (produit, newStock) => {
    if (newStock < 0) return;

    try {
      const response = await fetch(`/api/produits/${produit.id}/stock`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantite_stock_depart: newStock })
      });

      const data = await response.json();

      if (data.success) {
        await loadProduits();
      } else {
        showToast(`Erreur: ${data.message}`, 'error');
      }
    } catch (error) {
      showToast(`Erreur réseau: ${error.message}`, 'error');
    }
  };

  const handlePrintEtiquette = async (produit) => {
    try {
      const response = await fetch(
        `/api/print/etiquette-rayon?code_produit=${produit.code_produit}`
      );
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `etiquette_${produit.designation.replace(/ /g, '_')}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        showToast(`Étiquette générée pour ${produit.designation}`, 'success');
      } else {
        showToast('Erreur génération PDF', 'error');
      }
    } catch (error) {
      showToast(`Erreur: ${error.message}`, 'error');
    }
  };

  // ============================================================================
	const handleExportExcel = () => {
  	window.location.href = '/api/produits/export';
 	 showToast('📥 Téléchargement Excel en cours...', 'success');
	};

// ============================================================================
// IMPORT EXCEL
// ============================================================================
const handleImportClick = () => {
  fileInputRef.current?.click();
};

const handleImportExcel = async (event) => {
  const file = event.target.files?.[0];
  if (!file) return;

  setImporting(true);

  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch('/api/produits/import', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();

    if (data.success) {
      let msg = `✅ ${data.data.created} produits créés, ${data.data.updated} mis à jour`;
      if (data.data.errors && data.data.errors.length > 0) {
        msg += ` (${data.data.errors.length} erreurs)`;
        console.warn('Erreurs import:', data.data.errors);
      }
      showToast(msg, 'success');
      loadProduits(); // Recharger la liste
    } else {
      showToast(`❌ ${data.message}`, 'error');
    }
  } catch (error) {
    showToast('Erreur import fichier', 'error');
  } finally {
    setImporting(false);
    event.target.value = ''; // Reset pour permettre réimport
  }
};




  // ============================================================================
  // COMPOSANT TOAST
  // ============================================================================
  const Toast = () => {
    if (!toast.show) return null;

    const bgColor = toast.type === 'success' ? 'bg-green-500' : 
                    toast.type === 'error' ? 'bg-red-500' : 'bg-blue-500';

    return (
      <div className={`fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-fade-in`}>
        {toast.type === 'success' && '✅'}
        {toast.type === 'error' && '❌'}
        {toast.type === 'info' && 'ℹ️'}
        <span>{toast.message}</span>
        <button onClick={() => setToast({ ...toast, show: false })} className="ml-2">×</button>
      </div>
    );
  };

  // ============================================================================
  // COMPOSANT MODAL MÉDIA
  // ============================================================================
  const MediaModal = ({ produit }) => {
    if (!produit) return null;

    const hasImage = produit.image_lien;
    const isVideo = hasImage && (produit.image_lien.includes('.mp4') || produit.image_lien.includes('video'));

    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-auto">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-lg font-semibold">{produit.designation}</h3>
            <button onClick={() => setShowMediaModal(false)} className="text-gray-500">
              <X className="w-6 h-6" />
            </button>
          </div>
          
          <div className="p-6">
            {hasImage ? (
              <div className="space-y-4">
                {isVideo ? (
                  <video controls className="w-full max-h-96 rounded-lg" src={produit.image_lien}>
                    Votre navigateur ne supporte pas les vidéos.
                  </video>
                ) : (
                  <img 
                    src={produit.image_lien} 
                    alt={produit.designation}
                    className="w-full max-h-96 object-cover rounded-lg"
                  />
                )}
                
                <div className="text-sm text-gray-600">
                  <p><strong>URL:</strong> {produit.image_lien}</p>
                  <p><strong>Type:</strong> {isVideo ? 'Vidéo' : 'Image'}</p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <img 
                  src={STANDBY_IMAGE} 
                  alt="Aucune image"
                  className="w-32 h-32 mx-auto mb-4 opacity-50"
                />
                <p className="text-gray-500">Aucun média associé à ce produit</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Chargement des produits...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Toast />

      {/* ===== HEADER ===== */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-green-800">🏪 Gestion des Produits</h2>
      <div className="flex gap-2">
        <div className="flex flex-wrap gap-2">
          {/* Export Excel */}
          <button
            onClick={handleExportExcel}
            className="bg-emerald-600 text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-emerald-700 text-sm"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </button>

          {/* Import Excel */}
          <button
            onClick={handleImportClick}
            disabled={importing}
            className="bg-orange-500 text-white px-3 py-2 rounded-lg flex items-center gap-2 hover:bg-orange-600 disabled:opacity-50 text-sm"
          >
            <Upload className="w-4 h-4" />
            {importing ? 'Import...' : 'Import Excel'}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImportExcel}
            className="hidden"
          />
        </div>

        <button
          onClick={handleCreate}
          className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700"
        >
          <Plus className="w-5 h-5" />
          Nouveau Produit
        </button>
      </div>
    </div>

      {/* ===== STATISTIQUES ===== */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-green-100 p-4 rounded-lg">
          <div className="text-2xl font-bold text-green-800">{produits.length}</div>
          <div className="text-sm text-green-600">Produits total</div>
        </div>
        <div className="bg-blue-100 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-800">
            {produits.filter(p => p.actif === 'oui').length}
          </div>
          <div className="text-sm text-blue-600">Actifs</div>
        </div>
        <div className="bg-purple-100 p-4 rounded-lg">
          <div className="text-2xl font-bold text-purple-800">
            {produits.filter(p => p.origine === 'ferme').length}
          </div>
          <div className="text-sm text-purple-600">Ferme</div>
        </div>
        <div className="bg-orange-100 p-4 rounded-lg">
          <div className="text-2xl font-bold text-orange-800">
            {produits.filter(p => p.quantite_disponible <= 5).length}
          </div>
          <div className="text-sm text-orange-600">Stock faible</div>
        </div>
        <div className="bg-indigo-100 p-4 rounded-lg">
          <div className="text-2xl font-bold text-indigo-800">
            {produits.reduce((sum, p) => sum + (p.quantite_disponible * p.prix_ttc), 0).toFixed(2)}€
          </div>
          <div className="text-sm text-indigo-600">Valeur stock</div>
        </div>
      </div>
      {/* Encart workflow Excel */}
	<div className="bg-gradient-to-r from-emerald-50 to-orange-50 border border-emerald-200 rounded-lg p-4">
  	<div className="flex items-start gap-3">
   	 <FileSpreadsheet className="h-6 w-6 text-emerald-600 flex-shrink-0" />
    		<div className="text-sm">
    	 	 <span className="font-medium text-emerald-800">📊 Gestion par Excel : </span>
     	 	<span className="text-emerald-700">
        	Export Excel → Modifie hors ligne → Ajoute de nouvelles lignes → Import Excel
      		</span>
           </div>
  	</div>
	</div>

      {/* ===== RECHERCHE ET FILTRES ===== */}
      <div className="bg-white p-4 rounded-lg shadow space-y-4">
        {/* Barre de recherche */}
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="🔍 Rechercher un produit (nom ou code)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
            />
          </div>
          
          {/* Toggle vue */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded ${viewMode === 'table' ? 'bg-white shadow' : ''}`}
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${viewMode === 'grid' ? 'bg-white shadow' : ''}`}
            >
              <Grid className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filtres */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Filtre catégorie */}
          <select
            value={filterCategorie}
            onChange={(e) => setFilterCategorie(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">🏷️ Toutes catégories</option>
            <option value="none">📦 Sans catégorie</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.icone} {cat.nom} ({cat.nb_produits})
              </option>
            ))}
          </select>

          {/* Filtre origine */}
          <select
            value={filterOrigine}
            onChange={(e) => setFilterOrigine(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">🌍 Toutes origines</option>
            <option value="ferme">🚜 Ferme</option>
            <option value="partenaire">🤝 Partenaire</option>
          </select>

          {/* Filtre stock */}
          <select
            value={filterStock}
            onChange={(e) => setFilterStock(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">📦 Tous stocks</option>
            <option value="low">⚠️ Stock faible (≤5)</option>
            <option value="medium">📊 Stock moyen (6-20)</option>
            <option value="high">✅ Stock élevé (>20)</option>
          </select>

          {/* Filtre actif */}
          <select
            value={filterActif}
            onChange={(e) => setFilterActif(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">👁️ Tous statuts</option>
            <option value="oui">✅ Actifs</option>
            <option value="non">❌ Inactifs</option>
          </select>
        </div>

        {/* Compteur résultats */}
        <div className="text-sm text-gray-600">
          {filteredProduits.length} produit(s) trouvé(s) sur {produits.length} total
        </div>
      </div>

      {/* ===== LISTE PRODUITS (TABLE OU GRILLE) ===== */}
      {viewMode === 'table' ? (
        // VUE TABLEAU
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Catégorie</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prix</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Disponible</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Origine</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredProduits.map((produit) => {
                const categorie = categories.find(c => c.id === produit.categorie_id);
                return (
                  <tr key={produit.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4">
                      <div className="flex items-center">
                        <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center mr-3">
                          {produit.image_lien ? (
                            <button
                              onClick={() => {
                                setSelectedProduit(produit);
                                setShowMediaModal(true);
                              }}
                              className="w-full h-full rounded overflow-hidden hover:opacity-75"
                            >
                              {produit.image_lien.includes('video') || produit.image_lien.includes('.mp4') ? (
                                <Video className="w-6 h-6 text-purple-600" />
                              ) : (
                                <img 
                                  src={produit.image_lien} 
                                  alt={produit.designation}
                                  className="w-full h-full object-cover"
                                />
                              )}
                            </button>
                          ) : (
                            <img 
                              src={STANDBY_IMAGE} 
                              alt="Pas d'image"
                              className="w-full h-full object-cover opacity-40"
                            />
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{produit.designation}</div>
                          <div className="text-sm text-gray-500">Code: {produit.code_produit}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {categorie ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" 
                              style={{ backgroundColor: `${categorie.couleur}20`, color: categorie.couleur }}>
                          {categorie.icone} {categorie.nom}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">Non classé</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      {produit.prix_ttc}€/{produit.unite}
                    </td>
                    <td className="px-4 py-4">
                      <input
                        type="number"
                        value={produit.quantite_stock_depart}
                        onChange={(e) => handleStockAdjust(produit, parseFloat(e.target.value))}
                        className="w-20 px-2 py-1 border rounded text-sm"
                        min="0"
                        step="0.5"
                      />
                      <div className="text-xs text-gray-500">{produit.unite}</div>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        produit.quantite_disponible <= 5 
                          ? 'bg-red-100 text-red-800'
                          : produit.quantite_disponible <= 20
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {produit.quantite_disponible} {produit.unite}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs ${
                        produit.origine === 'ferme' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {produit.origine === 'ferme' ? '🚜 Ferme' : '🤝 Partenaire'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleToggleActif(produit)}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          produit.actif === 'oui' 
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {produit.actif === 'oui' ? (
                          <>
                            <Eye className="w-3 h-3 mr-1" />
                            Actif
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-3 h-3 mr-1" />
                            Inactif
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleView(produit)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Voir détails"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(produit)}
                          className="text-yellow-600 hover:text-yellow-800"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDuplicate(produit)}
                          className="text-green-600 hover:text-green-800"
                          title="Dupliquer"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handlePrintEtiquette(produit)}
                          className="text-purple-600 hover:text-purple-800"
                          title="Imprimer étiquette"
                        >
                          <Printer className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(produit)}
                          className="text-red-600 hover:text-red-800"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        // VUE GRILLE
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {filteredProduits.map((produit) => {
            const categorie = categories.find(c => c.id === produit.categorie_id);
            return (
              <div key={produit.id} className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow">
                {/* Image */}
                <div className="relative h-48 bg-gray-100 rounded-t-lg overflow-hidden">
                  {produit.image_lien ? (
                    <img 
                      src={produit.image_lien} 
                      alt={produit.designation}
                      className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                      onClick={() => {
                        setSelectedProduit(produit);
                        setShowMediaModal(true);
                      }}
                    />
                  ) : (
                    <img 
                      src={STANDBY_IMAGE} 
                      alt="Pas d'image"
                      className="w-full h-full object-cover opacity-30"
                    />
                  )}
                  
                  {/* Badge statut */}
                  <div className="absolute top-2 right-2">
                    <button
                      onClick={() => handleToggleActif(produit)}
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        produit.actif === 'oui' 
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-500 text-white'
                      }`}
                    >
                      {produit.actif === 'oui' ? '✅' : '❌'}
                    </button>
                  </div>

                  {/* Badge origine */}
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      produit.origine === 'ferme' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-blue-500 text-white'
                    }`}>
                      {produit.origine === 'ferme' ? '🚜' : '🤝'}
                    </span>
                  </div>
                </div>

                {/* Contenu */}
                <div className="p-4">
                  {/* Catégorie */}
                  {categorie && (
                    <div className="mb-2">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium" 
                            style={{ backgroundColor: `${categorie.couleur}20`, color: categorie.couleur }}>
                        {categorie.icone} {categorie.nom}
                      </span>
                    </div>
                  )}

                  {/* Nom produit */}
                  <h3 className="font-semibold text-lg text-gray-900 mb-1">{produit.designation}</h3>
                  <p className="text-xs text-gray-500 mb-2">Code: {produit.code_produit}</p>

                  {/* Prix */}
                  <div className="text-2xl font-bold text-green-600 mb-3">
                    {produit.prix_ttc}€
                    <span className="text-sm text-gray-500">/{produit.unite}</span>
                  </div>

                  {/* Stock */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-600">Stock:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      produit.quantite_disponible <= 5 
                        ? 'bg-red-100 text-red-800'
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {produit.quantite_disponible} {produit.unite}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(produit)}
                      className="flex-1 bg-yellow-500 text-white px-3 py-2 rounded hover:bg-yellow-600 flex items-center justify-center gap-1"
                    >
                      <Edit className="w-4 h-4" />
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDuplicate(produit)}
                      className="bg-green-500 text-white px-3 py-2 rounded hover:bg-green-600"
                      title="Dupliquer"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(produit)}
                      className="bg-red-500 text-white px-3 py-2 rounded hover:bg-red-600"
                      title="Supprimer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== MODAL FORMULAIRE ===== */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40 overflow-y-auto">
          <div className="bg-white rounded-lg max-w-3xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">
                {modalMode === 'create' ? '➕ Nouveau Produit' : 
                 modalMode === 'edit' ? '✏️ Modifier Produit' : '👁️ Détails Produit'}
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Ligne 1 : Désignation + Catégorie */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Désignation *
                  </label>
                  <input
                    type="text"
                    value={formData.designation}
                    onChange={(e) => setFormData({...formData, designation: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                    disabled={modalMode === 'view'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🏷️ Catégorie *
                  </label>
                  <select
                    value={formData.categorie_id}
                    onChange={(e) => setFormData({...formData, categorie_id: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    disabled={modalMode === 'view'}
                  >
                    <option value="">-- Choisir une catégorie --</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.icone} {cat.nom}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Ligne 2 : Origine + Fournisseur */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    🌍 Origine *
                  </label>
                  <select
                    value={formData.origine}
                    onChange={(e) => setFormData({...formData, origine: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    disabled={modalMode === 'view'}
                  >
                    <option value="ferme">🚜 Produit de la ferme</option>
                    <option value="partenaire">🤝 Produit partenaire</option>
                  </select>
                </div>

                {formData.origine === 'partenaire' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom du fournisseur/éleveur
                    </label>
                    <input
                      type="text"
                      value={formData.fournisseur}
                      onChange={(e) => setFormData({...formData, fournisseur: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="Ex: Ferme Martin"
                      disabled={modalMode === 'view'}
                    />
                  </div>
                )}
              </div>

              {/* Ligne 3 : Unité + Prix */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Unité * <span className="text-xs text-gray-500">(abréviation)</span>
                  </label>
                  <select
                    value={formData.unite}
                    onChange={(e) => setFormData({...formData, unite: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    disabled={modalMode === 'view'}
                  >
                    <optgroup label="Poids">
                      <option value="kg">kg - Kilogramme</option>
                      <option value="g">g - Gramme</option>
                    </optgroup>
                    <optgroup label="Volume">
                      <option value="L">L - Litre</option>
                      <option value="cL">cL - Centilitre</option>
                    </optgroup>
                    <optgroup label="Unités">
                      <option value="pce">pce - Pièce</option>
                      <option value="piece">piece - Pièce (ancien)</option>
                      <option value="bte">bte - Botte</option>
                      <option value="botte">botte - Botte (ancien)</option>
                      <option value="bqt">bqt - Bouquet</option>
                      <option value="sac">sac - Sachet</option>
                      <option value="paquet">paquet - Paquet (ancien)</option>
                      <option value="bqe">bqe - Barquette</option>
                      <option value="pot">pot - Pot</option>
                      <option value="lot">lot - Lot</option>
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix TTC (€) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.prix_ttc}
                    onChange={(e) => setFormData({...formData, prix_ttc: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    required
                    disabled={modalMode === 'view'}
                  />
                </div>
              </div>

              {/* Ligne 4 : Stock */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stock de départ *
                </label>
                <input
                  type="number"
                  step="0.5"
                  value={formData.quantite_stock_depart}
                  onChange={(e) => setFormData({...formData, quantite_stock_depart: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  required
                  disabled={modalMode === 'view'}
                />
              </div>

              {/* Upload image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📸 Image/Vidéo du produit
                </label>
                
                {modalMode !== 'view' && (
                  <div className="mb-3">
                    <input
                      type="file"
                      id="media-upload"
                      accept="image/*,video/*"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        if (file.size > 10 * 1024 * 1024) {
                          showToast('Fichier trop volumineux ! Maximum 10MB', 'error');
                          return;
                        }
                        
                        const uploadFormData = new FormData();
                        uploadFormData.append('media', file);
                        
                        try {
                          const response = await fetch('/api/site/upload-produit-media', {
                            method: 'POST',
                            body: uploadFormData
                          });
                          
                          const data = await response.json();
                          
                          if (data.success) {
                            setFormData(prev => ({
                              ...prev, 
                              image_lien: `${data.url}`
                            }));
                            showToast('Média uploadé avec succès !', 'success');
                          } else {
                            showToast(`Erreur : ${data.message}`, 'error');
                          }
                        } catch (error) {
                          showToast(`Erreur upload : ${error.message}`, 'error');
                        }
                      }}
                      className="hidden"
                    />
                    <label
                      htmlFor="media-upload"
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md cursor-pointer hover:bg-blue-700"
                    >
                      <Image className="w-4 h-4 mr-2" />
                      📤 Télécharger une image/vidéo
                    </label>
                  </div>
                )}
                
                {formData.image_lien ? (
                  <div className="mb-3">
                    <div className="flex items-start gap-3">
                      {formData.image_lien.includes('video') || formData.image_lien.includes('.mp4') ? (
                        <video 
                          src={formData.image_lien} 
                          className="w-32 h-32 object-cover rounded border"
                          controls
                        />
                      ) : (
                        <img 
                          src={formData.image_lien} 
                          alt="Prévisualisation"
                          className="w-32 h-32 object-cover rounded border"
                        />
                      )}
                      <div className="flex-1">
                        <input
                          type="url"
                          value={formData.image_lien}
                          onChange={(e) => setFormData(prev => ({...prev, image_lien: e.target.value}))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                          disabled={modalMode === 'view'}
                        />
                        {modalMode !== 'view' && (
                          <button
                            onClick={() => setFormData(prev => ({...prev, image_lien: ''}))}
                            className="mt-2 text-sm text-red-600 hover:text-red-800"
                          >
                            🗑️ Supprimer le média
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mb-3 p-4 bg-gray-50 rounded border border-gray-200">
                    <div className="flex items-center gap-3">
                      <img 
                        src={STANDBY_IMAGE} 
                        alt="Image par défaut"
                        className="w-24 h-24 object-cover rounded opacity-40"
                      />
                      <div className="text-sm text-gray-500">
                        <p className="font-medium">Image par défaut</p>
                        <p>standby.png sera affichée en l'absence d'image</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={3}
                  disabled={modalMode === 'view'}
                />
              </div>

              {/* Conseils cuisson */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conseils de cuisson
                </label>
                <textarea
                  value={formData.conseils_cuisson}
                  onChange={(e) => setFormData({...formData, conseils_cuisson: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={2}
                  disabled={modalMode === 'view'}
                />
              </div>

              {/* Recette producteur */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recette du producteur
                </label>
                <textarea
                  value={formData.recette_producteur}
                  onChange={(e) => setFormData({...formData, recette_producteur: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={2}
                  disabled={modalMode === 'view'}
                />
              </div>

              {/* Statut */}
              {modalMode !== 'view' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut
                  </label>
                  <select
                    value={formData.actif}
                    onChange={(e) => setFormData({...formData, actif: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="oui">✅ Actif</option>
                    <option value="non">❌ Inactif</option>
                  </select>
                </div>
              )}
            </div>

            {/* Boutons action */}
            {modalMode !== 'view' && (
              <div className="flex justify-end gap-3 p-6 border-t sticky bottom-0 bg-white">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center gap-2"
                >
                  <Save className="w-4 h-4" />
                  {modalMode === 'create' ? 'Créer' : 'Modifier'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Média */}
      {showMediaModal && <MediaModal produit={selectedProduit} />}
    </div>
  );
};

export default GestionProduits;